<template>
  <div
    class="offcanvas offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE'? $t('Create Expense'): $t('Edit Expense')  }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Expense Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-12">
            <label class="form-label">
              {{ $t('Branch') }}
            </label>
            <div class="custom-input-group custom-input-group input-group input-group-sm">
              <select2
                style="width: 100%;"
                v-model="v$.form.branch_id.$model"
                :options="select2Branches.options"
                :settings="select2Branches.setting"
                :placeholder="$t('Select Branch') + '...'"
              />
            </div>
          </div>
          <div class="col-12">
            <label class="form-label">
              {{ $t('Category') }}
              <span class="text-danger">*</span>
            </label>
            <div class="custom-input-group custom-input-group input-group input-group-sm">
              <select2
                style="width: 100%;"
                v-model="v$.form.expense_category_id.$model"
                :options="select2Categories.options"
                :settings="select2Categories.setting"
                :placeholder="$t('Select Category') + '...'"
              />
            </div>
            <div v-for="error of v$.form.expense_category_id.$errors"
              class="d-block invalid-feedback"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
          <div class="col-12">
            <label class="form-label">
              {{ $t('Expense Date') }}
              <span class="text-danger">*</span>
            </label>
            <date-range-picker
              v-model="form.expenseDateRange"
              :date-range="form.expenseDateRange"
              :single-date-picker="true"
              :ranges="false"
              :auto-apply="true"
              :show-dropdowns="true"
              :locale-data="dateRangeLocale"
              :control-container-class="''"
              @update="syncExpenseDate"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate">{{ formatDate(picker.startDate) }}</span>
                <span v-else class="text-muted">{{ $t('Select date') }}</span>
              </template>
            </date-range-picker>
            <div v-for="error of v$.form.expense_date.$errors"
              class="invalid-feedback d-block"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
          <div class="col-12">
            <label for="amount" class="form-label">
              {{ $t('Amount') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                v-model.number="v$.form.amount.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter amount') + '...'"
              >
              <div v-for="error of v$.form.amount.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <!--
            ใบกำกับภาษีซื้อ — โผล่เฉพาะเมื่อเลือกสาขาที่จด VAT แล้ว
            ร้าน/สาขาที่ไม่จด VAT ต้องไม่เห็นส่วนนี้เลย (silver-spoon-api/docs/vat-compliance.md Phase 2)
          -->
          <div class="col-12" v-if="shopIsVat">
            <div class="form-check form-switch">
              <input
                id="has_tax_invoice"
                class="form-check-input"
                type="checkbox"
                v-model="form.has_tax_invoice"
              >
              <label class="form-check-label" for="has_tax_invoice">
                {{ $t('Has a tax invoice') }}
              </label>
            </div>
            <div class="form-text">{{ $t('Turn this on to include this expense in the VAT purchase report.') }}</div>
          </div>

          <template v-if="showTaxInvoiceFields">
            <div class="col-12">
              <label class="form-label">{{ $t('Amount entered') }}</label>
              <div class="btn-group w-100" role="group">
                <input type="radio" class="btn-check" id="tax_included" value="included" v-model="form.tax_policy">
                <label class="btn btn-outline-secondary btn-sm" for="tax_included">{{ $t('Includes VAT') }}</label>
                <input type="radio" class="btn-check" id="tax_excluded" value="excluded" v-model="form.tax_policy">
                <label class="btn btn-outline-secondary btn-sm" for="tax_excluded">{{ $t('Excludes VAT') }}</label>
              </div>
              <!-- โชว์ผลคำนวณสดเพื่อให้คนคีย์เทียบกับใบกำกับตรงหน้าได้ทันที -->
              <div class="form-text">
                {{ $t('Base') }} {{ formatMoney(vatPreview.base) }}
                + {{ $t('VAT') }} {{ formatMoney(vatPreview.vat) }}
                = <span class="fw-semibold">{{ formatMoney(vatPreview.total) }}</span>
              </div>
            </div>

            <div class="col-12">
              <label class="form-label">
                {{ $t('Supplier') }}
                <span class="text-danger">*</span>
              </label>
              <div v-if="!form.supplier_manual">
                <select2
                  style="width: 100%;"
                  v-model="form.supplier_id"
                  :options="select2Suppliers.options"
                  :settings="select2Suppliers.setting"
                  :placeholder="$t('Select Supplier') + '...'"
                />
                <div class="form-text">
                  <span v-if="selectedSupplier && !selectedSupplier.tax_id" class="text-danger">
                    {{ $t('This supplier has no tax ID — add it on the supplier page first.') }}
                  </span>
                  <span v-else-if="selectedSupplier">{{ selectedSupplier.tax_id }}</span>
                  <a href="#" class="ms-2" @click.prevent="useManualSupplier">{{ $t('Type it in instead') }}</a>
                </div>
              </div>
              <div v-else class="row g-2">
                <div class="col-12">
                  <input
                    type="text"
                    v-model.trim="form.supplier_name"
                    class="form-control form-control-sm shadow-none"
                    :placeholder="$t('Supplier name') + '...'"
                  >
                </div>
                <div class="col-7">
                  <input
                    type="text"
                    v-model.trim="form.supplier_tax_id"
                    class="form-control form-control-sm shadow-none"
                    maxlength="13"
                    :placeholder="$t('Tax ID') + ' (13)'"
                  >
                </div>
                <div class="col-5">
                  <input
                    type="text"
                    v-model.trim="form.supplier_branch"
                    class="form-control form-control-sm shadow-none"
                    :placeholder="$t('Branch No')"
                  >
                </div>
                <div class="col-12 form-text">
                  <a href="#" @click.prevent="useRegisteredSupplier">{{ $t('Pick from supplier list') }}</a>
                </div>
              </div>
            </div>

            <div class="col-12">
              <label for="tax_invoice_no" class="form-label">
                {{ $t('Tax Invoice No.') }}
                <span class="text-danger">*</span>
              </label>
              <input
                id="tax_invoice_no"
                type="text"
                v-model.trim="form.tax_invoice_no"
                class="form-control form-control-sm shadow-none"
                :placeholder="$t('Number printed on the tax invoice') + '...'"
              >
            </div>

            <div class="col-12">
              <label for="tax_invoice_date" class="form-label">
                {{ $t('Tax Invoice Date') }}
                <span class="text-danger">*</span>
              </label>
              <input
                id="tax_invoice_date"
                type="date"
                v-model="form.tax_invoice_date"
                class="form-control form-control-sm shadow-none"
              >
              <div class="form-text">{{ $t('The date on the supplier document — this decides the filing month.') }}</div>
            </div>
          </template>

          <div class="col-12">
            <label for="reference_no" class="form-label">
              {{ $t('Reference No.') }}
            </label>
            <div class="input-group-sm">
              <input
                id="reference_no"
                type="text"
                v-model.trim="form.reference_no"
                class="form-control shadow-none"
                :placeholder="$t('Receipt or invoice number') + '...'"
              >
            </div>
          </div>
          <div class="col-12">
            <label for="payee" class="form-label">
              {{ $t('Payee') }}
            </label>
            <div class="input-group-sm">
              <input
                id="payee"
                type="text"
                v-model.trim="form.payee"
                class="form-control shadow-none"
                :placeholder="$t('Who was paid') + '...'"
              >
            </div>
          </div>
          <div class="col-12">
            <label for="message" class="form-label">
              {{ $t('Description') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="message"
                type="text"
                v-model.trim="v$.form.message.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter description') + '...'"
              >
              <div v-for="error of v$.form.message.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { Expenses } from '@/services/endpoint/expenses.service';
import numeral from 'numeral';
import Select2 from 'vue3-select2-component';
import DateRangePicker from 'vue3-daterange-picker';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      select2Branches: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
          allowClear: true
        }
      },
      select2Categories: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
          allowClear: true
        }
      },
      select2Suppliers: {
        options: [],
        setting: {
          allowClear: true
        }
      },
      // เก็บ list ดิบไว้ด้วย — ต้องใช้ tax_id ของผู้ขาย ซึ่ง select2 option (id/text) เก็บไม่ได้
      branches: [],
      suppliers: [],
      // ค่าภาษีเป็นของร้าน ไม่ใช่ของสาขา (api/docs/vat-compliance.md §2) — มาจาก filter.shop_tax
      shopTax: {
        tax_policy: 'no_tax',
        vat_rate: 0,
      },
      loading: true,
      hasPermission: false,
      loadingSave: false,
      form: {
        message: '',
        reference_no: '',
        payee: '',
        amount: '',
        expense_date: '',
        expense_category_id: '',
        branch_id: '',
        // --- ใบกำกับภาษีซื้อ (Phase 2) ---
        // has_tax_invoice / supplier_manual เป็น state ของ UI เท่านั้น ไม่ส่งขึ้น API
        has_tax_invoice: false,
        supplier_manual: false,
        tax_policy: 'included',
        supplier_id: '',
        supplier_name: '',
        supplier_tax_id: '',
        supplier_branch: '',
        tax_invoice_no: '',
        tax_invoice_date: '',
        expenseDateRange: { startDate: null, endDate: null }
      }
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    AccessDenied,
    Select2,
    DateRangePicker
  },
  computed: {
    dateRangeLocale() {
      return {
        firstDay: 1,
        format: 'yyyy-mm-dd',
        applyLabel: this.$t('Apply'),
        cancelLabel: this.$t('Cancel'),
        clearable: true,
        monthNames: this.$storeIndex.monthNames(),
        daysOfWeek: this.$storeIndex.daysOfWeek()
      };
    },
    // ร้านไม่จด VAT = เครดิตภาษีซื้อไม่ได้ ไม่ต้องเห็นส่วนใบกำกับเลย
    shopIsVat() {
      return !!this.shopTax.tax_policy && this.shopTax.tax_policy !== 'no_tax';
    },
    showTaxInvoiceFields() {
      return this.shopIsVat && this.form.has_tax_invoice;
    },
    selectedSupplier() {
      return this.suppliers.find(s => String(s.id) === String(this.form.supplier_id)) || null;
    },
    // แสดงผลคำนวณสดให้คนคีย์เทียบกับใบกำกับตรงหน้า — ตัวเลขที่บันทึกจริงคำนวณฝั่ง API
    // ด้วย SaleOrderHelper::calculatePrices() เสมอ ตรงนี้เป็นแค่ตัวช่วยตา
    vatPreview() {
      const rate = parseFloat(this.shopTax.vat_rate) || 0;
      const amount = parseFloat(this.form.amount) || 0;

      if (this.form.tax_policy === 'excluded') {
        const vat = amount * rate / 100;
        return { base: amount, vat, total: amount + vat };
      }

      const base = rate ? amount / (1 + rate / 100) : amount;
      return { base, vat: amount - base, total: amount };
    }
  },
  watch: {
    // sync expense_date จาก v-model ของ date-picker (เชื่อถือได้กว่า @update ที่ single-mode ไม่ค่อย fire)
    'form.expenseDateRange': {
      handler(val) {
        this.form.expense_date = (val && val.startDate) ? this.formatDate(val.startDate) : '';
        this.v$.form.expense_date.$touch();
      },
      deep: true
    },
  },
  methods: {
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatMoney(value) {
      return numeral(value || 0).format("0,0.00");
    },
    useManualSupplier() {
      this.form.supplier_manual = true;
      this.form.supplier_id = '';
    },
    useRegisteredSupplier() {
      this.form.supplier_manual = false;
      this.form.supplier_name = '';
      this.form.supplier_tax_id = '';
      this.form.supplier_branch = '';
    },
    syncExpenseDate(val) {
      this.form.expense_date = (val && val.startDate) ? this.formatDate(val.startDate) : '';
      this.v$.form.expense_date.$touch();
    },
    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d).slice(0, 10);
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${dt.getFullYear()}-${m}-${day}`;
    },
    async getCreateData() {
      await Expenses.getCreate().then(res => {
        let vm = this;
        this.shopTax = res.data.filter?.shop_tax || this.shopTax;
        this.branches = res.data.data.branches || [];
        this.suppliers = res.data.data.suppliers || [];
        this.select2Branches.options = res.data.data.branches.map(item => ({ id: item.id, text: vm.$t(item.name) }));
        this.select2Categories.options = res.data.data.categories.map(item => ({ id: item.id, text: vm.$t(item.name) }));
        this.select2Suppliers.options = this.suppliers.map(item => ({ id: item.id, text: item.name }));
        this.form.branch_id = this.select2Branches.options[0].id;
        this.form.expense_date = this.formatDate(Date.now());
        this.form.expenseDateRange = { startDate: Date.now() || null, endDate: Date.now() || null };
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    async getUpdateData() {
      await Expenses.getUpdate(this.id).then(res => {
        let vm = this;
        this.shopTax = res.data.filter?.shop_tax || this.shopTax;
        this.branches = res.data.filter.branches || [];
        this.suppliers = res.data.filter.suppliers || [];
        this.select2Branches.options = res.data.filter.branches.map(item => ({ id: item.id, text: vm.$t(item.name) }));
        this.select2Categories.options = res.data.filter.categories.map(item => ({ id: item.id, text: vm.$t(item.name) }));
        this.select2Suppliers.options = this.suppliers.map(item => ({ id: item.id, text: item.name }));
        const expenseDate = res.data.data.expense_date ? String(res.data.data.expense_date).slice(0, 10) : '';
        const taxPolicy = res.data.data.tax_policy || 'no_tax';
        this.form = {
          message: res.data.data.message,
          reference_no: res.data.data.reference_no,
          payee: res.data.data.payee,
          amount: res.data.data.amount,
          expense_date: expenseDate,
          expense_category_id: res.data.data.expense_category_id,
          branch_id: res.data.data.branch_id,
          // no_tax = ค่าใช้จ่ายทั่วไป (รวมของเก่าที่บันทึกก่อนมีฟีเจอร์นี้)
          has_tax_invoice: taxPolicy !== 'no_tax',
          // ผู้ขายที่กรอกเองคือแถวที่มีชื่อแต่ไม่ได้ผูกทะเบียน
          supplier_manual: !res.data.data.supplier_id && !!res.data.data.supplier_name,
          tax_policy: taxPolicy === 'no_tax' ? 'included' : taxPolicy,
          supplier_id: res.data.data.supplier_id,
          supplier_name: res.data.data.supplier_name,
          supplier_tax_id: res.data.data.supplier_tax_id,
          supplier_branch: res.data.data.supplier_branch,
          tax_invoice_no: res.data.data.tax_invoice_no,
          tax_invoice_date: res.data.data.tax_invoice_date,
          expenseDateRange: { startDate: expenseDate || null, endDate: expenseDate || null }
        };
        this.hasPermission = true;
      }).catch(err => {
        throw err;
      });
    },
    // API ปฏิเสธ key ที่ไม่ได้ประกาศไว้ใน middleware (ERROR_KEY_NOT_ALLOWED)
    // → ประกอบ payload เองทีละช่อง ห้าม clone ทั้ง form เพราะมี state ของ UI ปนอยู่
    // (has_tax_invoice / supplier_manual / expenseDateRange ไม่มีในฝั่ง API)
    setParamsBeforeSend() {
      const params = {
        message: this.form.message,
        amount: this.form.amount,
        expense_date: this.form.expense_date,
        expense_category_id: this.form.expense_category_id,
        // ไม่ติ๊ก "มีใบกำกับ" = ค่าใช้จ่ายทั่วไป ไม่เข้ารายงานภาษีซื้อ
        tax_policy: this.showTaxInvoiceFields ? this.form.tax_policy : 'no_tax',
      };

      if (this.form.reference_no) params.reference_no = this.form.reference_no;
      if (this.form.payee) params.payee = this.form.payee;
      if (this.form.branch_id) params.branch_id = this.form.branch_id;

      if (params.tax_policy !== 'no_tax') {
        params.tax_invoice_no = this.form.tax_invoice_no;
        params.tax_invoice_date = this.form.tax_invoice_date;

        if (this.form.supplier_manual) {
          params.supplier_name = this.form.supplier_name;
          params.supplier_tax_id = this.form.supplier_tax_id;
          if (this.form.supplier_branch) params.supplier_branch = this.form.supplier_branch;
        } else {
          params.supplier_id = this.form.supplier_id;
        }
      }

      return params;
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await Expenses.update(this.id, this.setParamsBeforeSend()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Expense!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
        this.loadingSave = false;
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      await Expenses.create(this.setParamsBeforeSend()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Expense!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  }
}
</script>
